import type { StorefrontProduct } from '../../api/client';
import { orderable } from '../../commerce/variantStock';
import type { ProductFacts } from './storeFacts';

// The choices a buyer makes before buying — Size: 30ml, 50ml — and which variant a set of picks means. Kept
// apart from the controls that draw them, because which pick leads to which variant is a rule, not a layout.

/** One variant of a product. */
type Variant = StorefrontProduct['variants'][number];

/** One choice the buyer makes: its name, and the values offered for it. */
export type ProductOption = { name: string; values: string[] };

/** The most choices a variant can carry, matching its option1 and option2. */
const MAX_OPTIONS = 2;

// Reads whether the product declares its choices, so variants are matched on option1/option2 rather than on their names.
const declares = (product: StorefrontProduct): boolean => (product.options ?? []).some((option) => option.name && option.values.length > 0);

// Lists the choices a buyer makes: the product's declared options, or one "Option" over its variants' names.
export function productOptions(product: StorefrontProduct): ProductOption[] {
  if (declares(product)) return (product.options ?? []).filter((option) => option.name && option.values.length > 0).slice(0, MAX_OPTIONS);
  return product.variants.length > 1 ? [{ name: 'Option', values: product.variants.map((variant) => variant.title) }] : [];
}

// Reads the value one variant holds for each of the product's choices.
export function variantValues(product: StorefrontProduct, variant: Variant): string[] {
  if (!declares(product)) return [variant.title];
  return [variant.option1, variant.option2].slice(0, productOptions(product).length).map((value) => value ?? '');
}

// Finds the variant whose values are exactly the picks, or null when no variant matches all of them.
export function variantFor(product: StorefrontProduct, picks: readonly string[]): Variant | null {
  return product.variants.find((variant) => variantValues(product, variant).every((value, index) => value === picks[index])) ?? null;
}

// Reports whether picking a value at one position, keeping the other picks, leads to a variant in stock.
export function valueInStock(product: StorefrontProduct, picks: readonly string[], index: number, value: string): boolean {
  const next = picks.map((pick, at) => (at === index ? value : pick));
  return orderable(variantFor(product, next) ?? undefined);
}

// Finds the variant one option's value leads to: the same other choices when that exists, else the first that can be ordered, else the first.
export function variantWithValue(product: StorefrontProduct, index: number, value: string, current: Variant | undefined): Variant | null {
  const picks = current ? [...variantValues(product, current)] : [];
  picks[index] = value;
  const exact = variantFor(product, picks);
  if (exact) return exact;
  const having = product.variants.filter((variant) => variantValues(product, variant)[index] === value);
  return having.find((variant) => orderable(variant)) ?? having[0] ?? null;
}

// Orders a card's pictures for a chosen variant: its own first, then those of no one variant, so hovering shows the product in use.
// Returns a card's facts once an option is chosen: no hover picture over the option's own, and a link to that option (a sample has no such page).
export function factsForChoice(facts: ProductFacts, variantId: string, isSample: boolean): ProductFacts {
  return { ...facts, hoverImage: '', href: isSample ? facts.href : `${facts.href}?variant=${variantId}` };
}

export function picturesForChoice(product: StorefrontProduct, imageId: string | null | undefined): StorefrontProduct['images'] {
  const featured = imageId ? product.images.find((picture) => picture.id === imageId) : undefined;
  if (!featured) return product.images;
  const tied = new Set(product.variants.map((variant) => variant.imageId).filter(Boolean));
  const rest = product.images.filter((picture) => picture !== featured);
  return [featured, ...rest.filter((picture) => !tied.has(picture.id)), ...rest.filter((picture) => tied.has(picture.id))];
}
